import {AfterViewInit, Component, Injectable, OnDestroy, OnInit} from '@angular/core';
import {TourApiService} from '../services/tour-api.service';
import {TourService} from '../services/tour.service';
import {VgAPI} from 'videogular2/core';
import {PlaybackQuality, TourVideoType} from '../model/tour';
import {Encoding} from '../model/encoding';
import {EnumAware} from '../decorators/enum-aware.decorator';
import {TrackingService} from '../services/tracking.service';
import {ActivatedRoute} from '@angular/router';
import {TourTagType} from '../model/tour-tag';

declare var VTTCue;

@Injectable()
@Component({
  selector: 'gt-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.css']
})

@EnumAware
export class VideoPlayerComponent implements OnInit, OnDestroy {

  TourVideoType = TourVideoType;
  api: VgAPI;
  sources: Array<Object> = [];
  textTrack: TextTrack;
  lastTimeUpdate = 0;
  sceneLabels: string[];
  encodings: Encoding[];
  quality: PlaybackQuality;
  floorplan: TourVideoType;
  builderLogoURL: string;
  videoPlaying = false;

  endedSubscription;
  seekedSubscription;
  videoHiddenSubscription;
  canPlaySubscription;
  playingSubscription;
  pausedSubscription;
  timeUpdateSubscription;
  cueChangedSubscription;

  constructor(public apiService: TourApiService,
              private tourService: TourService,
              private trackingService: TrackingService,
              private route: ActivatedRoute) {}

  ngOnInit() {
    this.sceneLabels = this.tourService.getSceneLabels(false);
    this.quality = this.tourService.tour.preferredPlaybackQuality;
    this.floorplan = this.tourService.tour.preferredPlaybackType;
    this.sources = this.tourService.getTourVideoSourceArrayForPreferredFloorplan();
    this.encodings = this.tourService.getTourVideoEncodingsForPreferredFloorplan();
    this.builderLogoURL = this.apiService.builderLogoURL();
    this.videoHiddenSubscription = this.tourService.videoVisibilityChanged.subscribe(
      (visible: boolean) => {
        (visible) ? this.api.getMediaById('tourVideo').play() : this.api.getMediaById('tourVideo').pause();
      }
    );
  }

  showFloorplan(tourType: TourVideoType) {
    this.tourService.tour.preferredPlaybackType = (tourType === TourVideoType.Fullframe)
                                                    ? TourVideoType.Fullframe : TourVideoType.Floorplan;
    this.sources = this.tourService.getTourVideoSourceArrayForPreferredFloorplan();
    this.encodings = this.tourService.getTourVideoEncodingsForPreferredFloorplan();
    this.floorplan = this.tourService.tour.preferredPlaybackType;
    this.trackingService.trackAction(((tourType === TourVideoType.Fullframe)
                                        ? TrackingService.kActionVideoFullScreen
                                        : TrackingService.kActionVideoFloorplan), null, 0);
  }

  playScene(index: number) {
    const scene = this.tourService.tour.findSceneByIndex(index);
    if (scene) {
      if (!this.videoPlaying) {
        this.api.getMediaById('tourVideo').play();
      }

      this.api.getMediaById('tourVideo').currentTime = scene.startTime + 1;
      this.tourService.updateCurrentSceneForName(scene.displayName);
      this.trackingService.trackAction(TrackingService.kActionGoToScene, scene.displayName, 0);
    }
  }

  changeQuality(quality: string) {
    for (const encoding of this.encodings) {
      if (quality === PlaybackQuality.HD.toString()) {
        this.tourService.tour.preferredPlaybackQuality = PlaybackQuality.HD;

      } else if (quality === PlaybackQuality.High.toString()) {
        this.tourService.tour.preferredPlaybackQuality = PlaybackQuality.High;

      } else if (quality === PlaybackQuality.Medium.toString()) {
        this.tourService.tour.preferredPlaybackQuality = PlaybackQuality.Medium;

      } else if (quality === PlaybackQuality.Low.toString()) {
        this.tourService.tour.preferredPlaybackQuality = PlaybackQuality.Low;
      }
    }
    this.quality = this.tourService.tour.preferredPlaybackQuality;
    this.sources = this.tourService.getTourVideoSourceArrayForPreferredFloorplan();
    this.trackingService.trackAction(TrackingService.kActionVideoQuality, quality, 0);
  }

  // onCueChange($event: any) {
  //   console.log('onCueChange');
  //   let currentCue: TextTrackCue;
  //   for (let i = 0, l = $event.target.activeCues.length; i < l; i++) {
  //     currentCue = $event.target.activeCues[ i ];
  //   }
  //   if (currentCue) {
  //     // console.log('onCueChange: ' + currentCue.text + ' phase: ' + $event.eventPhase);
  //     this.tourService.updateCurrentSceneForName(currentCue.text);
  //   } else {
  //     // console.log('onCueChange: [no currentCue] phase: ' + $event.eventPhase);
  //     this.tourService.updateCurrentSceneForName(null);
  //   }
  // }

  onEnterCue(event) {
    if (event) {
      // console.log('enter. : ' + event.currentTarget.text);
      this.tourService.updateCurrentSceneForName(event.currentTarget.text);
    }
  }

  onExitCue(event) {
    if (event) {
      // console.log('exit. : ' + event.currentTarget.text);
      this.tourService.updateCurrentSceneForName(null);
    }
  }

  onPlayerReady(api: VgAPI) {
    this.api = api;
    this.endedSubscription = this.api.getMediaById('tourVideo').subscriptions.ended.subscribe(
      () => {
        this.api.getMediaById('tourVideo').currentTime = 0;
      }
    );

    // IE doesn't support VTTCue objects
    // wrap the attempt in a try-catch and fall through
    // to crappy old player time updates
    try {

      this.textTrack = this.api.getMediaById('tourVideo').addTextTrack('subtitles', 'hidden');
      for (const scene of this.tourService.tour.sceneList) {
        const cue = new VTTCue(scene.startTime, scene.endTime, scene.displayName);
        cue.addEventListener('enter', (event) => {
          this.onEnterCue(event);
        });
        cue.addEventListener('exit', event => {
          this.onExitCue(event);
        });
        this.textTrack.addCue(cue);
      }

    } catch (e) {

      // console.log(e.message);
      /// subscribe to time updates
      this.timeUpdateSubscription = this.api.getMediaById('tourVideo').subscriptions.timeUpdate.subscribe(
        (event: Event) => {
          const now = new Date().getTime();
          const elasped = now - this.lastTimeUpdate;
          if (this.lastTimeUpdate === 0 || elasped > 1000) {
            this.tourService.updateCurrentScene(Math.round(this.api.getMediaById('tourVideo').currentTime));
            this.lastTimeUpdate = now;
          }
        }
      );
    }


    // subscribe to seeked updates
    this.seekedSubscription = this.api.getMediaById('tourVideo').subscriptions.seeked.subscribe(
      (event: Event) => {
        this.api.getMediaById('tourVideo').play();
      }
    );

    this.canPlaySubscription = this.api.getMediaById('tourVideo').subscriptions.canPlay.subscribe(
      (event: Event) => {
        if (this.tourService.resumeVideoFromCurrentTime) {
          this.tourService.resumeVideoFromCurrentTime = false;
          this.api.getMediaById('tourVideo').currentTime = this.tourService.currentVideoTime;

        // tour tag load
        } else if (this.tourService.tourTag) {
          if (this.tourService.tourTag.type === TourTagType.Scene) {
            const sceneIndex = this.tourService.tour.sceneList.map(function (e) {
              return e.id;
            }).indexOf(this.tourService.tourTag.sceneID);
            setTimeout(() => {
              this.playScene(sceneIndex);
            }, 1000);
          } else if (this.tourService.tourTag.type === TourTagType.Tour) {
            setTimeout(() => {
              this.api.getMediaById('tourVideo').play();
            }, 1000);
          }
          this.tourService.tourTag = null;
        }
      }
    );

    this.playingSubscription = this.api.getMediaById('tourVideo').subscriptions.playing.subscribe(
      (event: Event) => {
        this.videoPlaying = true;
      }
    );

    this.pausedSubscription = this.api.getMediaById('tourVideo').subscriptions.pause.subscribe(
      (event: Event) => {
        this.videoPlaying = false;
      }
    );
  }

  ngOnDestroy() {
    if (this.canPlaySubscription != null) {
      this.canPlaySubscription.unsubscribe();
    }
    if (this.endedSubscription != null) {
      this.endedSubscription.unsubscribe();
    }
    if (this.seekedSubscription != null) {
      this.seekedSubscription.unsubscribe();
    }
    if (this.videoHiddenSubscription != null) {
      this.videoHiddenSubscription.unsubscribe();
    }
    if (this.playingSubscription != null) {
      this.playingSubscription.unsubscribe();
    }
    if (this.pausedSubscription != null) {
      this.pausedSubscription.unsubscribe();
    }
    if (this.timeUpdateSubscription != null) {
      this.timeUpdateSubscription.unsubscribe();
    }
    if (this.cueChangedSubscription != null) {
      this.cueChangedSubscription.unsubscribe();
    }
    this.tourService.resumeVideoFromCurrentTime = true;
  }
}



// WEBPACK FOOTER //
// ./src/app/components/video-player.component.ts